import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, BarChart3 } from 'lucide-react';
import { apiFetch } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found. Please check your email link.');
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await apiFetch(API_ROUTES.auth.verifyEmail, {
        method: 'POST',
        body: { token },
        auth: false,
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed. The link may be expired.');
      }
    } catch (e) {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  // Visual config per status
  const config = {
    verifying: { icon: Loader, color: '#22d3ee', glow: 'rgba(34,211,238,0.25)', bg: 'rgba(34,211,238,0.12)' },
    success: { icon: CheckCircle, color: '#10b981', glow: 'rgba(16,185,129,0.3)', bg: 'rgba(16,185,129,0.12)' },
    error: { icon: XCircle, color: '#ef4444', glow: 'rgba(239,68,68,0.25)', bg: 'rgba(239,68,68,0.12)' },
  }[status];
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[140px] animate-float-slow" style={{ background: config.glow }} />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        <Link to="/" className="flex flex-col items-center gap-3 mb-8 group">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
            <BarChart3 size={26} className="text-white" />
          </div>
          <h1 className="font-display text-xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">TrackMyFxEA</h1>
        </Link>

        <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-white/15 via-white/5 to-transparent">
          <div className="rounded-3xl p-8 text-center backdrop-blur-xl" style={{
            background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
            boxShadow: `0 24px 60px rgba(0,0,0,0.5), 0 0 60px ${config.glow}`,
          }}>
            <div className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center" style={{
              background: config.bg,
              boxShadow: `0 0 40px ${config.glow}`,
            }}>
              <Icon size={40} style={{ color: config.color }} className={status === 'verifying' ? 'animate-spin' : ''} />
            </div>

            {status === 'verifying' && (
              <>
                <h2 className="font-display text-2xl font-bold text-white mb-2">Verifying your email...</h2>
                <p className="text-gray-400 text-sm">Please wait a moment.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <h2 className="font-display text-2xl font-bold text-white mb-3">Email Verified!</h2>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">{message}</p>
                <Link to="/login" className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all" style={{
                  background: 'linear-gradient(135deg, #10b981, #14b8a6)',
                  color: '#070912',
                  boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
                }}>
                  Go to Login
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <h2 className="font-display text-2xl font-bold text-white mb-3">Verification Failed</h2>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">{message}</p>
                <Link to="/login" className="inline-flex items-center gap-2 font-medium px-6 py-3 rounded-xl text-gray-300 transition-all" style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  Back to Login
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
